import * as React from "react";
import { cn } from "./cn";

type ImageFrameProps = React.HTMLAttributes<HTMLDivElement> & {
  src?: string | null;
  alt?: string;
  aspect?: string; // e.g. "16/9", "4/3"
  bordered?: boolean;
  fallback?: string;
  /**
   * Title to derive a monogram from when there is no image. Opt-in: callers
   * that pass it get a designed placeholder instead of the grey `fallback`
   * box.
   *
   * Worth having because "no image" is not an edge case on every portal — a
   * tenant with no Cloudflare Images configured has NO module imagery at all
   * (observed on avant2go), so the placeholder IS the design for 100% of its
   * cards, and a grey rectangle with a dash reads as breakage. Mirrors what
   * `siteLogos` already does for a missing logo: fall back to the name.
   */
  monogram?: string | null;
  /**
   * Load this image eagerly at high priority. Default stays lazy, which is
   * right for the many cards in a grid — but WRONG for an article hero, which
   * is the LCP element and was being lazy-loaded like a thumbnail.
   */
  priority?: boolean;
};

/** First letters of the first two words — "Samostojno delo logista" → "SD". */
function initialsOf(title: string): string {
  return title
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0] ?? "")
    .join("")
    .toUpperCase();
}

/**
 * Deliberately theme-INDEPENDENT: a quiet monogram in `foreground` at low
 * opacity, over the frame's existing grey.
 *
 * The first attempt tinted these with `accent` / `primary` for variety, which
 * looked right on one portal and broke on the next: `--color-accent` is a
 * per-portal theme value, and on the Elementum theme it resolves to a pale
 * blue-grey — `rgb(194,209,217)` on the frame's `rgb(220,220,220)`, i.e. an
 * invisible letterform. A placeholder that renders differently per tenant is
 * not a placeholder you can reason about, so this uses the one colour every
 * theme must keep legible against a light surface.
 */

export function ImageFrame({
  src,
  alt = "",
  aspect = "16/9",
  bordered = false,
  fallback,
  monogram,
  priority = false,
  className,
  style,
  ...props
}: ImageFrameProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden bg-light-gray w-full",
        bordered ? "border-[6px] border-gold" : null,
        className,
      )}
      style={{ aspectRatio: aspect, ...style }}
      {...props}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
        />
      ) : monogram ? (
        <div
          className={cn(
            "absolute inset-0 grid place-items-center font-display font-extrabold tracking-tight",
            // Scales with the frame rather than a fixed size — the same
            // component renders a 64px row thumb and a 400px card poster.
            "text-[clamp(0.75rem,32cqw,2.5rem)] [container-type:size]",
            "text-foreground/30",
          )}
          aria-hidden
        >
          {initialsOf(monogram)}
        </div>
      ) : (
        <div className="absolute inset-0 grid place-items-center text-medium-gray text-sm">
          {fallback ?? "No image"}
        </div>
      )}
    </div>
  );
}
